/**
 * Notes store — per-league personal notes
 *
 * My Notes rebuild, Stage 5 (Directional Change 11): notes now live on
 * JAI's servers, not localStorage. This file keeps its name and its
 * exported function names on purpose, so the callers that already use it
 * (ChatPage, TeamAnalyser, PlayerAnalyser, TradeAnalyser, OpponentScout,
 * InjuryInsurance, NotesPage) change as little as possible — but `addNote`
 * is now ASYNC and posts to the API. `loadNotes`/`saveNotes` no longer
 * touch localStorage for reading/writing the working set; `saveNotes` in
 * particular only ever made sense as a bulk localStorage overwrite, so it's
 * now a no-op with a console warning rather than silently doing nothing.
 *
 * A failed save must never silently swallow the error, and must never fall
 * back to localStorage — that would recreate the exact split-brain (some
 * notes on the server, some only in this browser) this stage exists to
 * remove. Every function here throws on failure; callers are responsible
 * for telling the user.
 *
 * MIGRATION (Directional Change 11): the OLD local keys (`jai_notes_<id>`,
 * and the legacy single-textarea `kai_notes_<id>`) are read once per league
 * by migrateLocalNotesIfNeeded and POSTed to the server, then a
 * `jai_notes_migrated_<id>` flag is set so it never runs twice. The local
 * keys themselves are deliberately NOT deleted here — kept for one release
 * as a safety net in case a migration silently missed something. Safe to
 * delete the raw read/write of STORAGE_KEY/OLD_STORAGE_KEY once that
 * release has shipped without incident (this comment is the marker for
 * whoever does that cleanup).
 */

import { leaguesApi } from './api'
import { storage } from './storage'

const STORAGE_KEY     = (id) => `jai_notes_${id}`
const OLD_STORAGE_KEY = (id) => `kai_notes_${id}`
const MIGRATED_FLAG   = (id) => `jai_notes_migrated_${id}`

const TITLE_MAX = 120

/** Mirrors the backend's notesService.js deriveTitleFromBody — same simple
 *  "first non-blank line, truncated" rule, kept in sync by hand since this
 *  repo has no code-sharing mechanism between frontend and backend. */
function deriveTitleFromText(text) {
  const firstLine = String(text || '').split('\n').map((l) => l.trim()).find((l) => l) || 'Untitled note'
  return firstLine.length > TITLE_MAX ? `${firstLine.slice(0, TITLE_MAX - 1)}…` : firstLine
}

/** All of this league's notes from the server, newest/pinned first (same
 *  ordering the API already returns). */
export async function loadNotes(leagueId) {
  const res = await leaguesApi.getNotes(leagueId)
  return res.data.notes
}

/** One note by id — NoteDetail.jsx's data source. Throws (404) if it
 *  doesn't exist or isn't this user's, same ownership floor as every other
 *  notes endpoint. */
export async function getNote(leagueId, noteId) {
  const res = await leaguesApi.getNote(leagueId, noteId)
  return res.data.note
}

/**
 * Bulk-overwrite made sense for a localStorage array; it doesn't correspond
 * to any real server operation, so this is now a deliberate no-op. Kept
 * (rather than removed) so an old call site fails loudly in the console
 * instead of throwing — none of the six rewired callers use it, but this
 * guards against a stray one this pass missed.
 */
export function saveNotes() {
  console.warn('[notesStore] saveNotes() no longer does anything — notes live on the server now. Use addNote/updateNote/deleteNote.')
}

/**
 * Prepend a new note for a league and persist it to the server. Returns the
 * created note (server shape: { id, leagueId, title, body, priority,
 * source, pinned, createdAt, updatedAt }).
 *
 * `extra` can carry { title, priority, source, pinned }. Per Directional
 * Change 11, every caller should pass a real, specific title ("Trade idea —
 * Player A for Player B") — the body-derived fallback here is a safety net
 * for a caller that doesn't, not the intended path.
 *
 * Throws on failure — callers must tell the user the save failed, never
 * swallow it and never fall back to localStorage.
 */
export async function addNote(leagueId, text, extra = {}) {
  const body = String(text || '').trim()
  const res = await leaguesApi.createNote(leagueId, {
    title: extra.title || deriveTitleFromText(body),
    body,
    priority: extra.priority || 'general',
    source: extra.source || 'manual',
    pinned: extra.pinned === true,
  })
  return res.data.note
}

/** Throws on failure — same contract as addNote. */
export async function updateNote(leagueId, noteId, fields) {
  const res = await leaguesApi.updateNote(leagueId, noteId, fields)
  return res.data.note
}

/** Throws on failure — same contract as addNote. */
export async function deleteNote(leagueId, noteId) {
  await leaguesApi.deleteNote(leagueId, noteId)
}

/**
 * One-time migration of this league's local notes to the server. Safe to
 * call on every NotesPage load — it's a no-op after the first successful
 * run (the flag short-circuits it), and if a note fails partway through,
 * the flag is deliberately left UNSET so the whole batch is retried next
 * load rather than silently losing notes (already-migrated notes are
 * skipped that time via the server-side body dedupe, not re-created).
 *
 * @returns {Promise<{migrated: boolean, count: number}>}
 */
// Guards against two overlapping runs for the same league in this tab —
// React StrictMode deliberately double-invokes effects in dev, and without
// this a NotesPage mount could kick off two independent migrations that
// each read the "what's already on the server" snapshot before either had
// written anything, creating duplicate notes. Keyed by leagueId, not global,
// so different leagues can still migrate concurrently.
const migrationInFlight = new Map()

export function migrateLocalNotesIfNeeded(leagueId) {
  if (migrationInFlight.has(leagueId)) return migrationInFlight.get(leagueId)
  const promise = runMigration(leagueId).finally(() => migrationInFlight.delete(leagueId))
  migrationInFlight.set(leagueId, promise)
  return promise
}

async function runMigration(leagueId) {
  if (await storage.getItem(MIGRATED_FLAG(leagueId))) return { migrated: false, count: 0 }

  let localNotes = []
  try {
    const raw = await storage.getItem(STORAGE_KEY(leagueId))
    if (raw) localNotes = JSON.parse(raw)
  } catch { /* corrupt local data — nothing to migrate from it */ }

  // The old single-textarea note, if it's still sitting there and isn't
  // already one of the cards above (loadNotes()'s own legacy migration used
  // to fold this in — see the old header comment this file used to have).
  // Native installs never had this key in the first place — nothing to find.
  const oldRaw = await storage.getItem(OLD_STORAGE_KEY(leagueId))
  if (oldRaw && oldRaw.trim() && !localNotes.some((n) => (n.text || '').trim() === oldRaw.trim())) {
    localNotes = [{ id: `legacy-${Date.now()}`, text: oldRaw.trim(), date: Date.now(), pinned: false }, ...localNotes]
  }

  if (!localNotes.length) {
    await storage.setItem(MIGRATED_FLAG(leagueId), '1')
    return { migrated: false, count: 0 }
  }

  // Don't re-create notes the server already has — protects against a
  // previous run that partially completed before it could set the flag.
  let existingBodies
  try {
    const serverNotes = await loadNotes(leagueId)
    existingBodies = new Set(serverNotes.map((n) => (n.body || '').trim()))
  } catch {
    // Can't tell what the server already has — abort WITHOUT setting the
    // flag so this is retried next load, rather than guessing.
    return { migrated: false, count: 0 }
  }

  let migratedCount = 0
  for (const note of localNotes) {
    const text = (note.text || '').trim()
    if (!text || existingBodies.has(text)) continue
    try {
      await leaguesApi.createNote(leagueId, {
        title: deriveTitleFromText(text),
        body: text,
        priority: 'general',
        source: note.source === 'chat' ? 'chat' : 'manual',
        pinned: note.pinned === true,
        createdAt: note.date ? new Date(note.date).toISOString() : undefined,
      })
      migratedCount += 1
    } catch (err) {
      console.warn('[notesStore] Migration stopped partway through — will retry the remainder next load:', err)
      return { migrated: migratedCount > 0, count: migratedCount }
    }
  }

  await storage.setItem(MIGRATED_FLAG(leagueId), '1')
  return { migrated: migratedCount > 0, count: migratedCount }
}
